import { z } from "zod";

import { Media } from "@/components/forms/schemas/media";
import { geodataNestedFormSchema } from "@/pages/geodata/schemas/geodata.nested.form";
import {
  accessibilityReadSchema,
  openingHoursReadSchema,
} from "@/pages/venues/schemas/venue-facts";
import { venueEnrichmentReadSchema } from "@/pages/venues/schemas/venue.aienrichment.form";

const STATUS_OPTIONS = [
  "Requested",
  "Unregistered",
  "Registered",
  "Complete",
] as const;

export const VENUE_SECTION_OPTIONS = [
  "Music",
  "Cinema",
  "Theater",
  "Sports",
] as const;

/**
 * READ schema for the venue edit/review pages.
 *
 * `.passthrough()` and a lenient nested `enrichment`, consumed through
 * `parseRead()`. Before the split this nested the *strict* enrichment schema
 * behind a hard `.parse()`, and `enrichmentStatus` was a required `z.string()`
 * — so a venue Tina created before the status was written blanked the whole
 * edit page. The strict contract lives on `venueEnrichmentSubmitSchema`.
 */
export const venueViewSchema = z
  .object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    address: z.string(),
    timezone: z.string(),
    mapsUrl: z.string().url(),
    geolocation: z.object({
      latitude: z.coerce.number(),
      longitude: z.coerce.number(),
    }),
    geodata: geodataNestedFormSchema,
    media: Media,
    phoneNumber: z.string().nullish(),
    about: z.string().nullish(),
    followers: z.coerce.number().default(0),
    hide: z.boolean().default(false),
    area: z.string().nullish(),
    // The id of the city area the venue is on (a part is allowed); the server writes `area` from it.
    areaId: z.string().nullish(),
    type: z.string().nullish(),
    // Seats (VenueDbDto.Capacity, an int > 0), not the enrichment `features.capacity` score.
    capacity: z.number().nullish(),
    // Weekly opening hours and wheelchair flags (TINA from Google, or a curator); see venue-facts.ts.
    openingHours: openingHoursReadSchema.nullish(),
    accessibility: accessibilityReadSchema.nullish(),
    // Server-derived keys (googleMapsCid, tina, ...) pass through; `wikidataId` is the venue's Wikidata
    // item (Q + digits) that TINA fills only on a safe match. It lives here, not at the top level.
    metadata: z
      .object({ wikidataId: z.string().nullish() })
      .passthrough()
      .nullish(),
    sections: z.array(z.enum(VENUE_SECTION_OPTIONS)).default([]),
    status: z.enum(STATUS_OPTIONS).default("Unregistered"),
    enrichmentStatus: z.string().nullish(),
    embeddingStatus: z.string().nullish(),
    enrichmentRetryCount: z.coerce.number().nullish(),
    enrichment: venueEnrichmentReadSchema.nullish(),
    createdBy: z.string().nullable().default("unknown"),
    createdAt: z.string().datetime({ offset: true }),
    updatedBy: z.string().nullish(),
    updatedAt: z.string().datetime({ offset: true }).nullish(),
  })
  .passthrough();

export type VenueViewType = z.infer<typeof venueViewSchema>;
